<template>
  <SrpModal v-model:isVisible="isVisibleProxy" size="large" isWithScroll :isClosable="false" maxHeight="calc(100dvh - 200px)">
    <template #header>
      <h2 class="global-h2 override-urls-modal__title">Override Link Urls</h2>
    </template>
    <template #content>
      <p class="override-urls-modal__hint">Set a custom url for each adventure. Leave blank to use the default Shrpa adventure page. Relative paths should start with /.</p>
      <div v-if="adventures.length === 0" class="override-urls-modal__empty">No adventures to configure.</div>
      <div v-else class="override-urls-modal__rows">
        <div v-for="adventure in adventures" :key="adventure.id" class="override-urls-modal__row">
          <img class="override-urls-modal__thumb" :src="coverSrc(adventure)" :alt="`Cover for ${adventure.title ?? 'adventure'}`" />
          <div class="override-urls-modal__fields">
            <div class="override-urls-modal__name">{{ adventure.title || "Untitled adventure" }}</div>
            <input
              v-model="draft[adventure.id]"
              class="override-urls-modal__input"
              type="text"
              placeholder="https://…"
              :aria-label="`Override URL for ${adventure.title ?? adventure.id}`"
              @blur="onUrlBlur(adventure.id)"
            />
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <SrpButton color="gray" fill="outlined" @click="onCancel" :isDisabled="isSaving">Cancel</SrpButton>
      <SrpButton @click="onSave" :isDisabled="isSaving">{{ isSaving ? "Saving…" : "Save" }}</SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { ItinerarySummary } from "@contracts/itinerary";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    adventures: ItinerarySummary[];
    urlOverrides: Record<string, string>;
    contentBaseUri: string;
    isSaving?: boolean;
  }>(),
  {
    adventures: () => [],
    urlOverrides: () => ({}),
    contentBaseUri: "",
    isSaving: false,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "save", value: Record<string, string>): void;
}>();

const isVisibleProxy = computed({
  get: () => props.isVisible,
  set: (v: boolean) => emit("update:isVisible", v),
});

const draft = ref<Record<string, string>>({});

watch(
  () => props.isVisible,
  visible => {
    if (!visible) return;
    const next: Record<string, string> = {};
    for (const adventure of props.adventures) {
      if (!adventure.id) continue;
      next[adventure.id] = props.urlOverrides?.[adventure.id] ?? "";
    }
    draft.value = next;
  }
);

function coverSrc(adventure: ItinerarySummary): string {
  const thumbnail = adventure.thumbnail?.toLowerCase() !== "thumbnocrop/" ? adventure.thumbnail : null;
  const path = thumbnail || "thumbnails/placeholder-image.jpg";
  return `${props.contentBaseUri}/cms/images/expeditions/${path}`;
}

function normalizeOverrideUrl(url: string): string {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return trimmed;
  if (trimmed.includes("://")) return trimmed;
  if (trimmed.startsWith("/") || trimmed.startsWith("./") || trimmed.startsWith("../")) return trimmed;

  const hostPart = trimmed.split(/[/?#]/, 2)[0] ?? "";
  // e.g. example.com, www.example.co.uk, example.com:8080
  if (/^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?$/.test(hostPart)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

function onUrlBlur(adventureId: string | null): void {
  if (!adventureId) return;
  draft.value[adventureId] = normalizeOverrideUrl(draft.value[adventureId] ?? "");
}

function onCancel(): void {
  emit("update:isVisible", false);
}

function onSave(): void {
  const cleaned: Record<string, string> = {};
  for (const [id, value] of Object.entries(draft.value)) {
    const normalized = normalizeOverrideUrl(value ?? "");
    draft.value[id] = normalized;
    if (normalized) cleaned[id] = normalized;
  }
  emit("save", cleaned);
}
</script>

<style scoped lang="scss">
.override-urls-modal__title {
  margin: 0;
}

.override-urls-modal__hint {
  margin: 0 0 16px;
  color: rgba(91, 91, 91, 1);
  font: 14px/18px sans-serif;
}

.override-urls-modal__empty {
  color: rgba(91, 91, 91, 1);
  font: 14px/18px sans-serif;
}

.override-urls-modal__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.override-urls-modal__row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.override-urls-modal__thumb {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
}

.override-urls-modal__fields {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.override-urls-modal__name {
  font: 600 13px/16px sans-serif;
  color: rgba(0, 0, 0, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.override-urls-modal__input {
  width: 100%;
  box-sizing: border-box;
  padding: 7px 10px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font: 13px/16px sans-serif;
  color: rgba(0, 0, 0, 0.85);
}
</style>
